import AppSaveBar from '@/components/AppSaveBar/AppSaveBar';
import Layout from '@/components/layout';
import SkeletonPage from '@/components/SkeletonPage';
import { config } from '@/config';
import { DEFAULT_DATE_RANGE } from '@/constants';
import { PricingPlan, ProductOverrideType } from '@/constants/enum';
import { handleScrollToElement, isAvailableFeature, openErrorToast, openToast, stringToArrayByComma, time24h } from '@/helpers';
import { checkDirtyField } from '@/helpers/compare';
import {
  useGetCrispDataQuery,
  useGetDetailSettingShippingQuery,
  useGetGeneralSettingQuery,
  useUpdateDetailSettingShippingMutation,
} from '@/redux/query';
import { errorFieldsSelector, handleResetErrorFields } from '@/redux/slice/error.slice';
import {
  currentShippingSelector,
  handleResetShipping,
  handleResetShippingInitial,
  handleResetShippingState,
  handleSaveBackupOverrideShippingDateRanges,
  handleSaveBackupOverrideShippingHolidays,
  handleSaveBackupOverrideShippingSpecificWorkingDays,
  handleSaveBackupShippingCollections,
  handleSaveBackupShippingHolidays,
  handleSaveBackupShippingProducts,
  handleSaveBackupShippingSettings,
  handleSaveBackupShippingSpecificWorkingDays,
  handleSaveBackupShippingWeeklyWorkingDays,
  handleSaveBackupShippingWeeklyWorkingDaysLastState,
  handleSaveOverrideShippingDateRanges,
  handleSaveOverrideShippingHolidays,
  handleSaveOverrideShippingSpecificWorkingDays,
  handleSaveShippingCollections,
  handleSaveShippingHolidays,
  handleSaveShippingProducts,
  handleSaveShippingSettings,
  handleSaveShippingSpecificWorkingDays,
  handleSaveShippingWeeklyWorkingDays,
  handleSaveShippingWeeklyWorkingDaysLastState,
  handleSetShippingDisable,
  handleSetShippingState,
  oldShippingSelector,
  shippingCollectionSelector,
  shippingHolidaySelector,
  shippingOverrideDateRangesSelector,
  shippingOverrideHolidaysSelector,
  shippingOverrideSpecificWorkingDaysSelector,
  shippingProductSelector,
  shippingSettingSelector,
  shippingSpecificWorkingDaySelector,
  shippingWeeklyWorkingDaySelector,
} from '@/redux/slice/shipping.slice';
import { IParamsApi } from '@/types/apis/params';
import { IShippingSettings } from '@/types/components/shipping';
import { BlockStack } from '@shopify/polaris';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import BlackoutDate from './components/BlackoutDates';
import { ProductAvailability } from './components/ProductAvailability';
import { ProductOverride } from './components/ProductOverride';
import ShippingAvailability from './components/ShippingAvailability';
import ShippingChoice from './components/ShippingChoice';
import ShippingMinimum from './components/ShippingMinimum';
import ShippingStatus from './components/ShippingStatus';
import ShippingTags from './components/ShippingTags';
import VisualShippingDays from './components/VisualShippingDays';
import { IWeeklyWorkingDay } from '@/types/components/day';

const ShippingSettingPage = () => {
  const { t } = useTranslation('shippingSettings');
  const dispatch = useDispatch();
  const { data: detailShippingSettingData, isLoading: isLoadingDetailSettingData } = useGetDetailSettingShippingQuery();
  const [updateDetailShippingSetting, { isLoading: isUpdateDetailSettingLoading }] = useUpdateDetailSettingShippingMutation();
  const shippingSettings = useSelector(shippingSettingSelector);
  const shippingWeeklyWorkingDays = useSelector(shippingWeeklyWorkingDaySelector);
  const shippingSpecificWorkingDays = useSelector(shippingSpecificWorkingDaySelector);
  const shippingHolidays = useSelector(shippingHolidaySelector);
  const errorFields = useSelector(errorFieldsSelector);
  const shippingProducts = useSelector(shippingProductSelector);
  const shippingCollections = useSelector(shippingCollectionSelector);
  const shippingOverrideSpecificWorkingDays = useSelector(shippingOverrideSpecificWorkingDaysSelector);
  const shippingOverrideHoliday = useSelector(shippingOverrideHolidaysSelector);
  const shippingOverrideDateRange = useSelector(shippingOverrideDateRangesSelector);
  const { data: generalSettingData, isLoading: isLoadingGeneralSettingData } = useGetGeneralSettingQuery(
    { shop: config.shop },
    { refetchOnMountOrArgChange: true },
  );
  const { data: crispData } = useGetCrispDataQuery({ shop: config.shop });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const currentShippingData = useSelector(currentShippingSelector);
  const oldShippingData = useSelector(oldShippingSelector);

  const isChangeData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { shippingState, ...oldRest } = oldShippingData;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { shippingState: currentShippingState, ...currentRest } = currentShippingData;
    return checkDirtyField(currentRest, oldRest).isDirty;
  }, [currentShippingData, oldShippingData]);

  const onSubmit = async () => {
    dispatch(handleSetShippingState({ isSaving: true, isLoading: true, isCheckingTimeSlot: true }));
    if (errorFields?.errorFields && Object.values(errorFields?.errorFields).some((item) => item)) {
      dispatch(handleSetShippingState({ isLoading: false }));
      if (errorFields.errorFields?.rule_addTimeSlot) {
        handleScrollToElement('shipping-time-slot');
        return;
      }
      if (errorFields.errorFields?.errorSpecificDays) {
        handleScrollToElement('shipping-specific-workingdays');
        return;
      }
      if (errorFields.errorFields?.errorSpecificProduct) {
        handleScrollToElement('shipping-specific-product');
        return;
      }
      if (errorFields.errorFields.emptyDateRange || errorFields.errorFields.emptyProductsAndCollections) {
        handleScrollToElement('shipping-override-rules');
        return;
      }
      return;
    }

    let overrideData;

    if (Number(shippingSettings.overrideType) === ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS) {
      overrideData = shippingOverrideSpecificWorkingDays.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        productIds: item.productIds,
        collectionIds: item.collectionIds,
        overrideDates: item.overrideDates,
      }));
    } else if (Number(shippingSettings.overrideType) === ProductOverrideType.OVERRIDE_HOLIDAYS) {
      overrideData = shippingOverrideHoliday.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        productIds: item.productIds,
        collectionIds: item.collectionIds,
        overrideDates: item.overrideDates,
      }));
    } else {
      overrideData = shippingOverrideDateRange.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        productIds: item.productIds,
        collectionIds: item.collectionIds,
        overridePreparationDay: item.overridePreparationDay,
        overrideDeliveryDay: item.overrideDeliveryDay,
      }));
    }

    const dataSubmit: IParamsApi.IUpdateDetailShippingSetting = {
      shop: shippingSettings.shop,
      additionalNote: shippingSettings.additionalNote || '',
      isActive: shippingSettings.isActive,
      isActiveDatePicker: shippingSettings.isActiveDatePicker,
      minimumOrderAmount:
        shippingSettings.minimumOrderAmount === '' ||
          shippingSettings.minimumOrderAmount === null ||
          isNaN(Number(shippingSettings.minimumOrderAmount))
          ? null
          : Number(shippingSettings.minimumOrderAmount),
      dateChoice: shippingSettings.shippingChoice,
      workingDayType: shippingSettings.workingDayType,
      timeSlotType: shippingSettings.shippingTimeSlotType,
      preparationTimeType: shippingSettings.shippingPreparationTimeType,
      orderLimitType: shippingSettings.orderLimitType,
      overrideRuleType: Number(shippingSettings.overrideType),
      productAvailabilityType: shippingSettings.productAvailabilityType,
      dateRange: {
        unit: shippingSettings.shippingDateRange.unit,
        value: Number(shippingSettings.shippingDateRange.value),
      },
      isEnablePreparationTime: shippingSettings.isEnablePreparationTime,
      additionalTags: shippingSettings.additionalTags,
      holidays: shippingHolidays.map((item) => ({
        year: item.year,
        month: item.month,
        dates: item.dates,
      })),
      specificWorkingDays: shippingSpecificWorkingDays.map((item) => ({
        year: item.year,
        month: item.month,
        dates: item.dates,
      })),
      weeklyWorkingDays: shippingWeeklyWorkingDays.map((item) => ({
        isEnable: item.isEnable,
        isEnableCutOffTime: item.isEnableCutOffTime,
        cutOffTime: item.cutOffTime,
        cutOffPreparationTime: {
          unit: item.cutOffPreparationTime.unit,
          value: Number(item.cutOffPreparationTime.value),
        },
        day: item.day,
        workingHoursStart: item.workingHours.start,
        workingHoursEnd: item.workingHours.end,
        orderLimitPerDay: item.orderLimitPerDay,
        preparationTime: {
          unit: item.preparationTime.unit,
          value: Number(item.preparationTime.value),
        },
        dateRange: {
          unit: item.dateRange.unit,
          value: Number(item.dateRange.value),
        },
        exactTimeSlots: item.exactTimes,
        timeSlots: item.timeSlots,
      })),
      productAvailabilityProducts: shippingProducts,
      productAvailabilityCollections: shippingCollections,
      overrideRules: overrideData,
    };

    updateDetailShippingSetting(dataSubmit)
      .unwrap()
      .then(() => {
        dispatch(handleResetShippingState());
        openToast(dispatch);
      })
      .catch(() => {
        openErrorToast(dispatch);
        dispatch(handleResetShippingState());
      });
  };

  const handleDiscard = () => {
    dispatch(handleResetErrorFields());
    dispatch(handleResetShipping());
  };

  useEffect(() => {
    if (!detailShippingSettingData?.data) {
      console.log('Data is undefined');
      dispatch(handleResetShippingInitial());
      return;
    }

    const shippingSettingData: IShippingSettings = {
      id: detailShippingSettingData.data.id,
      shop: detailShippingSettingData.data.shop,
      name: detailShippingSettingData.data.name || t('shippingSettings.locationAddress.defaultZone'),
      locationId: detailShippingSettingData.data.locationId,
      isActive: detailShippingSettingData.data.isActive,
      isActiveDatePicker: detailShippingSettingData.data.isActiveDatePicker,
      shippingChoice: detailShippingSettingData.data.dateChoice,
      workingDayType: detailShippingSettingData.data.workingDayType,
      shippingTimeSlotType: detailShippingSettingData.data.timeSlotType,
      shippingPreparationTimeType: detailShippingSettingData.data.preparationTimeType,
      overrideType: detailShippingSettingData.data.overrideRuleType,
      shippingDateRange: {
        unit: detailShippingSettingData.data.dateRange?.unit,
        value: detailShippingSettingData.data.dateRange?.value.toString() || DEFAULT_DATE_RANGE.toString(),
      },
      productAvailabilityType: detailShippingSettingData.data.productAvailabilityType,
      isEnablePreparationTime: detailShippingSettingData.data.isEnablePreparationTime,
      additionalTags: stringToArrayByComma(detailShippingSettingData.data.additionalTags),
      updatedAt: detailShippingSettingData.data.updatedAt,
      createdAt: detailShippingSettingData.data.createdAt,
      orderLimitType: detailShippingSettingData.data.orderLimitType,
      minimumOrderAmount: detailShippingSettingData.data.minimumOrderAmount?.toString() || '',
    };

    const shippingWeeklyWorkingDaysData: IWeeklyWorkingDay[] =
      detailShippingSettingData.data.weeklyWorkingDays?.map((item: any) => ({
        id: item.id,
        settingId: item.settingId,
        isEnable: item.isEnable,
        day: item.day,
        workingHours: {
          end: item.workingHoursEnd,
          start: item.workingHoursStart,
        },
        preparationTime: {
          unit: item.preparationTime?.unit,
          value: item.preparationTime?.value,
        },
        isEnableCutOffTime: item.isEnableCutOffTime,
        cutOffTime: item.cutOffTime,
        cutOffPreparationTime: {
          unit: item.cutOffPreparationTime?.unit,
          value: item.cutOffPreparationTime?.value,
        },
        dateRange: {
          unit: item.dateRange?.unit,
          value: item.dateRange?.value,
        },
        exactTimes: item.exactTimeSlots,
        timeSlots: item.timeSlots,
        orderLimitPerDay: item.orderLimitPerDay,
      })) || [];

    const specificWorkingDaysData =
      detailShippingSettingData.data.specificWorkingDays?.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        year: item.year,
        month: item.month,
        dates: stringToArrayByComma(item.dates).map((date) => Number(date)),
        updatedAt: item.updatedAt,
        createdAt: item.createdAt,
      })) || [];

    const holidayData =
      detailShippingSettingData.data.holidays?.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        year: item.year,
        month: item.month,
        dates: stringToArrayByComma(item.dates).map((date) => Number(date)),
        updatedAt: item.updatedAt,
        createdAt: item.createdAt,
      })) || [];

    const shippingProductData = detailShippingSettingData.data.productAvailabilityProducts;
    const shippingCollectionData = detailShippingSettingData.data.productAvailabilityCollections;

    if (detailShippingSettingData.data.overrideRules) {
      const shippingOverrideDateRule =
        detailShippingSettingData.data.overrideRules?.map((item) => ({
          id: item.id,
          settingId: item.settingId,
          productIds: item.productIds,
          collectionIds: item.collectionIds,
          overrideDates: item.overrideDates,
        })) || [];

      const shippingOverrideTimeRule =
        detailShippingSettingData.data.overrideRules?.map((item) => ({
          id: item.id,
          settingId: item.settingId,
          productIds: item.productIds,
          collectionIds: item.collectionIds,
          overridePreparationDay: item.overridePreparationDay,
          overrideDeliveryDay: item.overrideDeliveryDay,
        })) || [];

      if (detailShippingSettingData.data.overrideRuleType === ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS) {
        dispatch(handleSaveOverrideShippingSpecificWorkingDays(shippingOverrideDateRule));
        dispatch(handleSaveBackupOverrideShippingSpecificWorkingDays(shippingOverrideDateRule));
      } else if (detailShippingSettingData.data.overrideRuleType === ProductOverrideType.OVERRIDE_HOLIDAYS) {
        dispatch(handleSaveOverrideShippingHolidays(shippingOverrideDateRule));
        dispatch(handleSaveBackupOverrideShippingHolidays(shippingOverrideDateRule));
      } else {
        dispatch(handleSaveOverrideShippingDateRanges(shippingOverrideTimeRule));
        dispatch(handleSaveBackupOverrideShippingDateRanges(shippingOverrideTimeRule));
      }
    }

    dispatch(handleSaveShippingSettings(shippingSettingData));
    dispatch(handleSaveShippingWeeklyWorkingDays(shippingWeeklyWorkingDaysData));
    dispatch(handleSaveShippingWeeklyWorkingDaysLastState(shippingWeeklyWorkingDaysData));
    dispatch(handleSaveShippingSpecificWorkingDays(specificWorkingDaysData));
    dispatch(handleSaveShippingHolidays(holidayData));
    dispatch(handleSaveShippingProducts(shippingProductData));
    dispatch(handleSaveShippingCollections(shippingCollectionData));
    dispatch(handleSaveBackupShippingSettings(shippingSettingData));
    dispatch(handleSaveBackupShippingWeeklyWorkingDays(shippingWeeklyWorkingDaysData));
    dispatch(handleSaveBackupShippingWeeklyWorkingDaysLastState(shippingWeeklyWorkingDaysData));
    dispatch(handleSaveBackupShippingSpecificWorkingDays(specificWorkingDaysData));
    dispatch(handleSaveBackupShippingHolidays(holidayData));
    dispatch(handleSaveBackupShippingProducts(shippingProductData));
    dispatch(handleSaveBackupShippingCollections(shippingCollectionData));
    dispatch(handleResetShippingState());
  }, [detailShippingSettingData, dispatch, t]);

  useEffect(() => {
    dispatch(
      handleSetShippingDisable({
        isDisableSpecificProduct: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
        isDisableProductOverride: !isAvailableFeature(PricingPlan.Smart, generalSettingData?.data.plan as PricingPlan),
        isDisableOrderLimitPerTimeSlot: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
        isDisableMinimumOrderValue: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
      }),
    );
  }, [dispatch, generalSettingData?.data.plan]);

  return (
    <Layout title={t('titleBar')}>
      <AppSaveBar open={isChangeData} onSave={onSubmit} onDiscard={handleDiscard} loading={isUpdateDetailSettingLoading} />
      {isLoadingDetailSettingData || isLoadingGeneralSettingData ? (
        <SkeletonPage />
      ) : (
        <BlockStack gap="400">
          <ShippingStatus />
          {shippingSettings.isActive && shippingSettings.isActiveDatePicker && (
            <>
              <ShippingChoice />
              <ShippingMinimum currency={crispData?.data.currency || ''} />
              <ShippingAvailability time24h={time24h} />
              <VisualShippingDays time24h={time24h} />
              <ProductAvailability />
              <ProductOverride />
              <BlackoutDate />
              <ShippingTags />
            </>
          )}
        </BlockStack>
      )}
    </Layout>
  );
};

export default ShippingSettingPage;
